import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/CreateAdmin.css";
import axios from "axios";
import { GridLoader } from "react-spinners";

function AdminDash() {
  const navigate = useNavigate();

  const [availableGroupIds, setAvailableGroupIds] = useState([]);

  const [newUser, setNewUser] = useState({
    unitName: "",
    email: "",
    employeeId: "",
    name: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const baseURL = sessionStorage.getItem("baseURL");

  /* -------------------- AUTH & LOAD -------------------- */

  useEffect(() => {
    if (sessionStorage.getItem("isLoggedIn") !== "true") {
      navigate("/");
    } else if (sessionStorage.getItem("isSuperAdmin") === "true") {
      navigate("/super-admin");
    } else if (sessionStorage.getItem("isAdmin") !== "true") {
      navigate("/dashboard");
    } else {
      fetchGroupIds();
    }
  }, [navigate]);

  const fetchGroupIds = async () => {
    try {
      document.getElementById("loading").hidden = false;
      const res = await axios.get(baseURL + "pu/getGroupIds");
      setAvailableGroupIds(res.data.map((u) => u.production_unit_name));
    } catch (err) {
      console.error("Error fetching group IDs", err);
      setAvailableGroupIds([]);
    } finally {
      document.getElementById("loading").hidden = true;
    }
  };

  /* -------------------- HANDLERS -------------------- */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: false }));
  };

  /* -------------------- VALIDATION -------------------- */

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateFields = () => {
    document.getElementById("loading").hidden = false;
    const errors = {};
    if (!newUser.unitName.trim()) errors.unitName = "Production Unit Name is required";
    if (!newUser.email.trim()) errors.email = "Email is required";
    else if (!validateEmail(newUser.email.trim())) errors.email = "Invalid email format";
    if (!newUser.name.trim()) errors.name = "Name is required";
    if (!newUser.employeeId.trim()) errors.employeeId = "Employee ID is required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  function showToast(message, type = "success") {
    const toast = document.createElement("div");
    toast.innerText = message;
    const bg =
      type === "error" ? "#C0392B" :
        type === "warning" ? "#e67e22" :
          "#1a6b3c";
    Object.assign(toast.style, {
      position: "fixed",
      top: "20px",
      right: "20px",
      background: bg,
      color: "#fff",
      padding: "12px 20px",
      borderRadius: "8px",
      boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
      zIndex: 9999,
      opacity: "0",
      fontSize: "0.92rem",
      fontFamily: "'Source Sans 3', system-ui, sans-serif",
      fontWeight: "600",
      letterSpacing: "0.01em",
      transition: "opacity 0.3s ease",
    });
    document.body.appendChild(toast);
    requestAnimationFrame(() => (toast.style.opacity = "1"));
    setTimeout(() => {
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 300);
    }, 3500);
  }

  /* -------------------- SUBMIT -------------------- */

  const handleAddProductionUnit = async () => {
    if (!validateFields()) {
      document.getElementById("loading").hidden = true;
      return;
    }
    document.getElementById("add-user").disabled = true;
    const formData = new FormData();
    formData.append("productionUnitName", newUser.unitName);
    formData.append("email", newUser.email);
    formData.append("employeeId", newUser.employeeId.toLowerCase());
    formData.append("name", newUser.name);

    try {
      axios
        .post(baseURL + "auth/addUsers", formData, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        })
        .then(() => {
          document.getElementById("loading").hidden = true;
          showToast("User added successfully");
          navigate("/users");
        });
    } catch (err) {
      console.error("Add User failed", err);
      showToast("Error adding user. Please try again.", "error");
    } finally {
      document.getElementById("loading").hidden = true;
    }
  };

  /* -------------------- UI -------------------- */

  return (
    <div className="container-fluid py-4">
      <div className="card admin-page">
        <h2 className="text-center">Add User</h2>

        {/* ── Production Unit ── */}
        <div className="mb-3">
          <label htmlFor="unitName">Production Unit *</label>
          <select
            id="unitName"
            className={`form-control ${fieldErrors.unitName ? "is-invalid" : ""}`}
            value={newUser.unitName}
            name="unitName"
            onChange={handleInputChange}
          >
            <option value="">Select production unit</option>
            {availableGroupIds.map((unit) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>
          {fieldErrors.unitName && (
            <div className="invalid-feedback">{fieldErrors.unitName}</div>
          )}
        </div>

        {/* ── Employee ID ── */}
        <div className="mb-3">
          <label htmlFor="employeeId">Employee ID *</label>
          <input
            id="employeeId"
            type="text"
            className={`form-control ${fieldErrors.employeeId ? "is-invalid" : ""}`}
            name="employeeId"
            value={newUser.employeeId}
            onChange={handleInputChange}
            placeholder="Enter employee ID"
            autoComplete="off"
          />
          {fieldErrors.employeeId && (
            <div className="invalid-feedback">{fieldErrors.employeeId}</div>
          )}
        </div>

        {/* ── Name ── */}
        <div className="mb-3">
          <label htmlFor="name">Name *</label>
          <input
            id="name"
            type="text"
            className={`form-control ${fieldErrors.name ? "is-invalid" : ""}`}
            name="name"
            value={newUser.name}
            onChange={handleInputChange}
            placeholder="Enter full name"
          />
          {fieldErrors.name && (
            <div className="invalid-feedback">{fieldErrors.name}</div>
          )}
        </div>

        {/* ── Email ── */}
        <div className="mb-3">
          <label htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            className={`form-control ${fieldErrors.email ? "is-invalid" : ""}`}
            name="email"
            value={newUser.email}
            onChange={handleInputChange}
            placeholder="Enter email address"
          />
          {fieldErrors.email && (
            <div className="invalid-feedback">{fieldErrors.email}</div>
          )}
        </div>

        {/* ── Actions ── */}
        <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
          <button className="btn btn-secondary" onClick={() => navigate("/users")}>
            Cancel
          </button>
          <button className="btn btn-primary" id="add-user" onClick={handleAddProductionUnit}>
            Add User
          </button>
        </div>
      </div>

      {/* ── Loading ── */}
      <div className="loading" id="loading">
        <GridLoader color="var(--c-navy)" size={18} />
      </div>
    </div>
  );
}

export default AdminDash;